import { useEffect, useRef } from 'react';
import useOfflineQueue from '@conductor/hooks/useOfflineQueue';
import { useTiger } from '@shared/context/mobile/useTiger';
import styles from './OfflineSync.module.scss';

/**
 * Drenado GLOBAL de la cola offline. Vive en el layout (siempre montado), así las operaciones
 * encoladas desde CUALQUIER pantalla (iniciar viaje, items, evidencias, etc.) se reenvían al
 * recuperar señal aunque el chofer no esté en /mapa-chofer. Muestra cuántas quedan pendientes.
 *
 * El guard de procesamiento de la cola es a nivel de módulo, así que este drenador y el de
 * PageMapChofer nunca procesan en simultáneo (sin doble envío).
 */
export default function OfflineSync() {
    const { procesarCola, cantidadPendiente } = useOfflineQueue();
    const tiger = useTiger();
    const isOnline = tiger.isOnline.value;
    const wasOnline = useRef(isOnline);

    // Reintento al recuperar conexión (bridge nativo Tiger).
    useEffect(() => {
        if (isOnline && !wasOnline.current) procesarCola();
        wasOnline.current = isOnline;
    }, [isOnline, procesarCola]);

    // Reintento al evento 'online' del navegador.
    useEffect(() => {
        const onOnline = () => procesarCola();
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
    }, [procesarCola]);

    // Reintento periódico mientras haya pendientes y señal.
    useEffect(() => {
        if (!cantidadPendiente) return;
        const id = setInterval(() => { if (navigator.onLine) procesarCola(); }, 30000);
        return () => clearInterval(id);
    }, [cantidadPendiente, procesarCola]);

    if (!cantidadPendiente) return null;

    return (
        <div className={styles.pendientes} role="status">
            <i className="bi bi-cloud-arrow-up" />
            <span>
                {cantidadPendiente} {cantidadPendiente > 1 ? 'operaciones pendientes' : 'operación pendiente'} de enviar
            </span>
        </div>
    );
}
